import React, { useState, useEffect } from 'react';
import {
  useGetAccountsQuery,
  useDeleteAccountMutation,
  useCreateAccountMutation,
  useUpdateAccountMutation,
  useGetAccountQuery
} from '../redux/services/accountsApi';
import { useToast } from '../context/ToastContext';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControlLabel,
  Switch,
  Chip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

// Local storage key for filters
const STORAGE_KEY = 'accountFilters';

const getSavedFilters = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {
      page: 1,
      limit: 25,
    };
  } catch (error) {
    return {
      page: 1,
      limit: 25,
    };
  }
};

const AccountFormDialog = ({ open, handleClose, accountId, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    active: true,
    token_api: '',
    group_id: '',
  });
  const [errors, setErrors] = useState({});
  const { data: accountData } = useGetAccountQuery(accountId, { skip: !accountId });
  const [createAccount] = useCreateAccountMutation();
  const [updateAccount] = useUpdateAccountMutation();
  const { showToast } = useToast();

  useEffect(() => {
    if (accountData?.data) {
      setFormData({
        name: accountData.data.name || '',
        active: accountData.data.active ?? true,
        token_api: accountData.data.token_api || '',
        group_id: accountData.data.group_id || '',
      });
    }
  }, [accountData]);

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.token_api.trim()) {
      newErrors.token_api = 'API Token is required';
    }
    if (!formData.group_id) {
      newErrors.group_id = 'Group ID is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    try {
      const submitData = {
        ...formData,
        group_id: parseInt(formData.group_id, 10)
      };

      if (accountId) {
        await updateAccount({ id: accountId, ...submitData }).unwrap();
        showToast('Account updated successfully', 'success');
      } else {
        await createAccount(submitData).unwrap();
        showToast('Account created successfully', 'success');
      }
      onSuccess();
      handleClose();
    } catch (error) {
      console.error('Error saving account:', error);
      showToast(error.data?.message || 'Error saving account', 'error');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{accountId ? 'Edit Account' : 'Create Account'}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                error={!!errors.name}
                helperText={errors.name}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="API Token"
                name="token_api"
                value={formData.token_api}
                onChange={handleInputChange}
                error={!!errors.token_api}
                helperText={errors.token_api}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Group ID"
                name="group_id"
                type="number"
                value={formData.group_id}
                onChange={handleInputChange}
                error={!!errors.group_id}
                helperText={errors.group_id}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.active}
                    onChange={handleInputChange}
                    name="active"
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="submit" variant="contained" color="primary">
            {accountId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const Accounts = () => {
  const [filters, setFilters] = useState(getSavedFilters());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const { showToast } = useToast();

  const { data: accountsData, isLoading } = useGetAccountsQuery(filters);
  const [deleteAccount] = useDeleteAccountMutation();

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSearch = () => {
    setFilters((prev) => ({
      ...prev,
      page: 1,
    }));
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    handleFilterChange('page', newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    handleFilterChange('limit', newRowsPerPage);
  };

  const handleDelete = async (accountId) => {
    if (window.confirm('Are you sure you want to delete this account?')) {
      try {
        await deleteAccount(accountId).unwrap();
        showToast('Account deleted successfully', 'success');
      } catch (error) {
        console.error('Error deleting account:', error);
        showToast(error.data?.message || 'Error deleting account', 'error');
      }
    }
  };

  const handleOpenCreateDialog = () => {
    setSelectedAccountId(null);
    setDialogOpen(true);
  };

  const handleOpenEditDialog = (accountId) => {
    setSelectedAccountId(accountId);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedAccountId(null);
  };

  const handleSuccess = () => {
    // Refresh the data
    handleSearch();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Accounts</Typography>
        <Button variant="contained" color="primary" onClick={handleOpenCreateDialog}>
          Create Account
        </Button>
      </Box>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : accountsData?.data?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No accounts found
                  </TableCell>
                </TableRow>
              ) : (
                accountsData?.data?.map((account) => (
                  <TableRow key={account.id}>
                    <TableCell>{account.name}</TableCell>
                    <TableCell>
                      <Chip
                        label={account.active ? 'Active' : 'Inactive'}
                        color={account.active ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{new Date(account.updated).toLocaleString()}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleOpenEditDialog(account.id)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDelete(account.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={accountsData?.data?.length || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <AccountFormDialog
        open={dialogOpen}
        handleClose={handleCloseDialog}
        accountId={selectedAccountId}
        onSuccess={handleSuccess}
      />
    </Box>
  );
};

export default Accounts; 